/**
 * Illustrated icons for the top-level mobile nav sections (Guides, Tools,
 * Products, Blog, About).
 *
 * Drawn in the same visual language as the per-tool icons in
 * components/tools/tool-illustration.tsx: viewBox 0 0 36 36, the shared
 * green/ink palette, #f0fdf4 card-body fills, plus the brand's playful grass
 * sprouts and warm sun accents, so the section rows and the Tools sub-rows read
 * as one icon system inside the drawer. Rendered at the native 38px but sized
 * down to the tile via a parent [&>svg]:h-6 selector.
 */

const G = "#16a34a" // green-600
const G_DARK = "#15803d" // green-700
const G_LIGHT = "#86efac" // green-300
const SUN = "#fbbf24" // amber-400
const GRAY = "#9ca3af"
const INK = "#0f1d14"

const NAV_SECTION_SVG: Record<string, React.ReactNode> = {
  // Interactive specimen: an isometric soil cutaway with visible grass roots,
  // an anatomy marker, and an orbit arc to distinguish it from Community.
  "grass-atlas": (
    <svg
      width="38"
      height="38"
      viewBox="0 0 36 36"
      fill="none"
      aria-hidden="true"
      data-nav-section-illustration="grass-atlas"
    >
      <path
        d="M7 12.2C9.2 5.8 16.3 3.4 22.3 5.2c4.1 1.2 7 4.1 7.8 7.3"
        stroke={G_LIGHT}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeDasharray="2.2 2.4"
      />
      <path d="m29.2 10.3 1.2 2.5-2.8-.3" stroke={G} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />

      <path d="m7 20 10.7-4.3L29 19.5l-10.8 4.4L7 20Z" fill="#86c873" stroke={INK} strokeWidth="1.1" strokeLinejoin="round" />
      <path d="m7 20 11.2 3.9v7L7 26.8V20Z" fill="#a87947" stroke={INK} strokeWidth="1.1" strokeLinejoin="round" />
      <path d="m18.2 23.9 10.8-4.4v7l-10.8 4.4v-7Z" fill="#805a38" stroke={INK} strokeWidth="1.1" strokeLinejoin="round" />

      <path d="M17.7 18.3c-3.4-3.3-5.2-6.4-5.6-9.2 3.8 1.8 6 5 6.7 9.3" fill={G} />
      <path d="M18.5 18.5c-.4-5 .5-9 2.8-12 1.3 4.7.6 8.7-1.7 12.1" fill={G_DARK} />
      <path d="M19.6 18.8c2-3.8 4.6-6.2 7.7-7.4-1 4-3.2 6.6-6.6 7.9" fill="#4ade80" />
      <path d="M16.4 24.3c-1.1 2.1-2.6 3.6-4.5 4.4M19 24.4c.2 2.4-.2 4.2-1.2 5.5M21.2 23.5c1.1 1.8 2.5 3 4.3 3.7"
        stroke="#f5d89f"
        strokeWidth="1.1"
        strokeLinecap="round"
      />

      <circle cx="27.2" cy="16.1" r="3" fill="#f0fdf4" stroke={G_DARK} strokeWidth="1.2" />
      <path d="M27.2 14.8v2.6M25.9 16.1h2.6" stroke={G_DARK} strokeWidth="1" strokeLinecap="round" />
    </svg>
  ),
  // Open book with a little sprout growing from the spine + a sun.
  guides: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path
        d="M18 12C15 10 10.6 9.6 7 10.5V27.5c3.6-.9 8-.5 11 1.5 3-2 7.4-2.4 11-1.5V10.5C26.4 9.6 21 10 18 12Z"
        stroke={INK}
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="#f0fdf4"
      />
      <path d="M18 12V29" stroke={INK} strokeWidth="1.4" />
      <path d="M9.8 15.5h5M9.8 18.6h5M9.8 21.7h4" stroke={G_LIGHT} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M21.2 15.5h5M21.2 18.6h5M22.8 21.7h3.4" stroke={G_LIGHT} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M18 11.5V6" stroke={G_DARK} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M18 9.4c-1.5.1-2.8-1-3-2.7 1.7-.1 2.9 1 3 2.7Z" fill={G} />
      <path d="M18 8c1.4-.2 2.4-1.5 2.3-3.1-1.5 0-2.5 1.3-2.3 3.1Z" fill={G_DARK} />
      <circle cx="27.4" cy="7.2" r="1.7" fill={SUN} />
    </svg>
  ),
  // Toolbox with a handle, a green latch, and a grass sprig poking out.
  tools: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path
        d="M25 12c-.3-2.2.2-3.9 1.6-5.1M27.8 12c.7-1.9 2-3.2 3.6-3.7"
        stroke={G}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M13.5 12v-1A2.4 2.4 0 0 1 15.9 8.6h4.2A2.4 2.4 0 0 1 22.5 11v1"
        stroke={INK}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect x="5" y="12" width="26" height="16" rx="2.4" stroke={INK} strokeWidth="1.6" fill="#f0fdf4" />
      <path d="M5 19h26" stroke={INK} strokeWidth="1.4" />
      <rect x="15.5" y="16.5" width="5" height="5" rx="1.2" fill={G} />
    </svg>
  ),
  // Shopping bag with a plant sprig poking out the top and a green check.
  products: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path
        d="M8.5 12.5h19l-1.3 15a2.4 2.4 0 0 1-2.4 2.2H12.2a2.4 2.4 0 0 1-2.4-2.2Z"
        stroke={INK}
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="#f0fdf4"
      />
      <path d="M12.8 13.5v-2a5.2 5.2 0 0 1 10.4 0v2" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M18 12.5c-.1-2.4-1.3-4.1-3.3-5" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.4 9.3c-1.4-.1-2.5-1.3-2.5-2.9 1.5 0 2.6 1.2 2.5 2.9Z" fill={G} />
      <path d="M16.6 10.8c1.2-.4 1.9-1.7 1.6-3.1-1.3.3-2 1.6-1.6 3.1Z" fill={G_DARK} />
      <path d="M14.2 21.3l2.4 2.4 5-5" stroke={G} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Article page with a folded corner and an amber pencil scribbling.
  blog: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path
        d="M9 5.5h10.5L26 12v13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V7.5a2 2 0 0 1 2-2Z"
        stroke={INK}
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="#f0fdf4"
      />
      <path d="M19.5 5.5V12h6" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill={G_LIGHT} />
      <path d="M10.8 15.5h8M10.8 19h6.5" stroke={GRAY} strokeWidth="1.3" strokeLinecap="round" />
      <path
        d="M17.5 28.4l2.1 2.1 7.5-7.5-2.1-2.1z"
        fill={SUN}
        stroke={INK}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M17 31l2.6-.5-2.1-2.1z" fill={G} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M22.9 23l2.1 2.1" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  // Phone with a grass sprout on screen + a sun peeking past the corner.
  app: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="10.5" y="5.5" width="15" height="25" rx="3" stroke={INK} strokeWidth="1.6" fill="#f0fdf4" />
      <path d="M15.5 8.2h5" stroke={GRAY} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M18 24.5c-1.3-2.7-2.2-4.4-2.7-6.2" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M18 24.5c-.3-3.2-.5-5.2-.5-7.2" stroke={G_DARK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M18 24.5c.4-3.5.6-5.7.5-7.6" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M18 24.5c1.3-2.7 2.2-4.4 2.7-6.2" stroke={G_DARK} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.9 24.7Q18 25.6 20.1 24.7" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <path d="M15 28h6" stroke={GRAY} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="28.6" cy="8.6" r="1.9" fill={SUN} />
    </svg>
  ),
  // Dandelion clock: a seed-head puffball on a stem with a couple of jagged
  // weed leaves and a sun accent — reads as "weed" against the grass sprouts.
  weeds: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* stem */}
      <path d="M16.6 16.5C16 22 16 26.5 16.6 30.5" stroke={G_DARK} strokeWidth="1.6" strokeLinecap="round" />
      {/* seed spokes radiating from the puffball */}
      <g stroke={G} strokeWidth="1" strokeLinecap="round">
        <path d="M16.5 12 16.5 3.4M16.5 12 10.5 4.8M16.5 12 22.5 4.8M16.5 12 6.6 9M16.5 12 26.4 9M16.5 12 8.2 15.4M16.5 12 24.8 15.4M16.5 12 13 3.9M16.5 12 20 3.9" />
      </g>
      <g fill={G_DARK}>
        <circle cx="16.5" cy="3.4" r="1" /><circle cx="10.5" cy="4.8" r="1" /><circle cx="22.5" cy="4.8" r="1" />
        <circle cx="6.6" cy="9" r="1" /><circle cx="26.4" cy="9" r="1" /><circle cx="13" cy="3.9" r="0.9" /><circle cx="20" cy="3.9" r="0.9" />
      </g>
      {/* puffball globe drawn over the spoke roots */}
      <circle cx="16.5" cy="12" r="4.6" fill="#f0fdf4" stroke={INK} strokeWidth="1.4" />
      {/* jagged weed leaves at the base */}
      <path d="M16.4 26C13 25 10.6 24.4 8 25.4L10 23.4 8.2 23.2 10.2 21.4" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M16.4 27.6C19.4 26.8 21.6 26.2 24 27L22.2 25.2 24 25 22.2 23.4" stroke={G} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="27.6" cy="7.4" r="1.7" fill={SUN} />
    </svg>
  ),
  // Lawn beetle: split wing cases with green spots, antennae, and legs, over a
  // couple of grass sprouts — the bug itself, distinct from the weeds puffball.
  pests: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* antennae */}
      <path d="M15.6 8.6C14.2 7 12.8 6.2 11 6M20.4 8.6C21.8 7 23.2 6.2 25 6" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      {/* legs */}
      <path d="M11.8 15.5 7.6 13.6M11.4 19.5H6.8M12 23.2 8 25.6M24.2 15.5l4.2-1.9M24.6 19.5h4.6M24 23.2l4 2.4" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      {/* head */}
      <circle cx="18" cy="10.6" r="2.9" fill={G_LIGHT} stroke={INK} strokeWidth="1.4" />
      {/* body with split wing cases */}
      <path d="M18 12.6c4 0 6.6 3 6.6 6.9 0 4.3-2.9 7.5-6.6 7.5s-6.6-3.2-6.6-7.5c0-3.9 2.6-6.9 6.6-6.9Z" fill="#f0fdf4" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M18 12.8v14" stroke={INK} strokeWidth="1.3" />
      {/* wing spots */}
      <circle cx="15.2" cy="17.2" r="1.1" fill={G} />
      <circle cx="20.8" cy="17.2" r="1.1" fill={G} />
      <circle cx="14.9" cy="21.6" r="1" fill={G_DARK} />
      <circle cx="21.1" cy="21.6" r="1" fill={G_DARK} />
      {/* grass sprouts at the base */}
      <path d="M7 32c0-2.4.8-4 1.8-5M10 32c0-2 .6-3.2 1.4-4M26 32c0-2.4-.8-4-1.8-5M29 32c0-2-.6-3.2-1.4-4" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="29.4" cy="7.4" r="1.7" fill={SUN} />
    </svg>
  ),
  // Brand badge: a grass tuft under a little sun.
  about: (
    <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="18" cy="18" r="12.4" stroke={INK} strokeWidth="1.6" fill="#f0fdf4" />
      <path d="M18 24.2C16.3 20.7 15.1 18.5 14.5 16.2" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M18 24.2C17.5 20.1 17.2 17.5 17.2 15" stroke={G_DARK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M18 24.2C18.4 19.7 18.7 16.9 18.6 14.5" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M18 24.2C18.8 20.1 19.5 17.7 20.2 15.6" stroke={G_DARK} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M18 24.2C19.7 20.7 20.9 18.5 21.5 16.2" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.4 24.4Q18 25.5 20.6 24.4" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <circle cx="24" cy="12.4" r="1.9" fill={SUN} />
    </svg>
  ),
}

export function hasNavSectionIllustration(section: string): boolean {
  return section in NAV_SECTION_SVG
}

export function NavSectionIllustration({ section }: { section: string }) {
  return <>{NAV_SECTION_SVG[section] ?? null}</>
}
